import { StatsService } from '../../../core/services/stats.service';
import { Component, Input, OnInit, inject } from '@angular/core';
import { BotsService } from '../../../core/services/bots.service';
import { DatePipe } from '@angular/common';
import { Bot, Compagnie, Stat } from '@shared/models';
import { ChartData } from 'chart.js';
import { combineLatest, first, startWith } from 'rxjs';
import { SchedulerService } from '@core/services/scheduler.service';
import { AuthService } from '@core/services/auth.service';
import { Scheduler } from '@shared/models/scheduler.model';
import { MatCard, MatCardHeader, MatCardTitle } from '@angular/material/card';
import { IntroCardComponent } from '../intro-card/intro-card.component';
import { SuccessLineChartComponent } from '../success-line-chart/success-line-chart.component';
import { SuccessRateCardComponent } from '../success-rate-card/success-rate-card.component';
import { DashboardBotThumbComponent } from '../dashboard-bot-thumb/dashboard-bot-thumb.component';
import { MatAnchor } from '@angular/material/button';
import { RouterLink } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { FormatFileSizePipe } from '../../pipes/format-file-size.pipe';
import { FormatTimePipe } from '../../pipes/format-time.pipe';
import { HasRoleDirective } from '../../directives/has-role.directive';

@Component({
  selector: 'app-dashboard-user',
  templateUrl: './dashboard-user.component.html',
  styleUrls: ['./dashboard-user.component.scss'],
  imports: [
    MatCard,
    MatCardHeader,
    MatCardTitle,
    IntroCardComponent,
    SuccessLineChartComponent,
    SuccessRateCardComponent,
    DashboardBotThumbComponent,
    MatAnchor,
    RouterLink,
    MatIcon,
    FormatFileSizePipe,
    FormatTimePipe,
    HasRoleDirective,
  ],
})
export class DashboardUserComponent implements OnInit {
  @Input() compagnyInput: Compagnie;

  protected barChartData: ChartData<'line'>;

  protected successRateData: {
    successRate: number;
    successLength: number;
    retryLength: number;
    failLength: number;
  };

  protected startDate: Date = this.setMidnight(this.minusSevenDays(new Date()));

  protected endDate: Date = this.yesterday(new Date());

  protected bots: Bot[] | null;

  protected totalAnalysis: number | null;

  protected schedulerNumber: number;

  protected company: Compagnie;

  private readonly schedulerService = inject(SchedulerService);

  private readonly authService = inject(AuthService);

  private readonly botsService = inject(BotsService);

  private readonly datePipe = inject(DatePipe);

  private readonly statsService = inject(StatsService);

  ngOnInit(): void {
    const user = this.authService.user$.getValue();
    this.company = (user?.compagnie as Compagnie) ?? this.compagnyInput;

    // TODO: changer en call pour récuperer tous les schedulers
    this.schedulerService
      .getAll({
        page: 1,
        limit: 50,
        isActive: true,
        compagnyId: this.company.id,
      })
      .pipe(
        startWith({
          data: [] as Scheduler[],
          hasNextPage: false,
          length: 0,
          page: 1,
        })
      )
      .subscribe((schedulers) => {
        this.schedulerNumber = schedulers?.data.reduce(
          (acc: number, scheduler: Scheduler) => {
            if (scheduler.days.includes(7)) {
              return acc + 7 * scheduler.times.length;
            }

            return acc + scheduler.days.length * scheduler.times.length;
          },
          0
        );
      });

    this.botsService
      .getAll({
        page: 1,
        limit: 20,
        compagnyId: this.company.id,
      })
      .pipe(
        startWith({ data: [] as Bot[], hasNextPage: false, length: 0, page: 1 })
      )
      .subscribe((bots) => {
        this.bots = bots.data;
        this.getAllAnalyses();
      });
  }

  dateRangeChange(): void {
    this.getAllAnalyses();
  }

  private getAllAnalyses(): void {
    const startDate = this.startDate.toISOString();
    const endDate = this.endDate.toISOString();

    if (!this.bots) {
      return;
    }
    const arrStats = this.bots.map((bot) =>
      this.statsService.getAll(bot.id as string, startDate, endDate)
    );

    combineLatest(arrStats)
      .pipe(first())
      .subscribe((stats) => {
        const onlyBotWithStats = stats.sort((a, b) => b.length - a.length);
        this.totalAnalysis = onlyBotWithStats.reduce(
          (acc, item: Stat[]) =>
            acc +
            item.reduce((accbis, itemBis: Stat) => accbis + itemBis.total, 0),
          0
        );

        const successRate =
          Math.trunc(
            (onlyBotWithStats.reduce((acc, item: Stat[]) => {
              const subSequences = item.reduce((accbis, itemBis: Stat) => {
                return accbis + itemBis.successPercentage;
              }, 0);
              const zeroVerif =
                item.length !== 0 ? subSequences / item.length : 0;
              return acc + zeroVerif;
            }, 0) /
              onlyBotWithStats.length) *
              100
          ) / 100;

        const successLength = onlyBotWithStats.reduce(
          (acc, item: Stat[]) =>
            acc +
            item.reduce((accbis, itemBis: Stat) => accbis + itemBis.success, 0),
          0
        );
        const failLength = onlyBotWithStats.reduce(
          (acc, item: Stat[]) =>
            acc +
            item.reduce((accbis, itemBis: Stat) => accbis + itemBis.failed, 0),
          0
        );
        const retryLength = onlyBotWithStats.reduce(
          (acc, item: Stat[]) =>
            acc +
            item.reduce((accbis, itemBis: Stat) => accbis + itemBis.retry, 0),
          0
        );

        this.successRateData = {
          successRate,
          successLength,
          retryLength,
          failLength,
        };

        const date = onlyBotWithStats[0].map((stat: Stat) =>
          this.datePipe.transform(stat.date, 'shortDate')
        );

        const chartSuccess: number[] = [];
        const chartErrors: number[] = [];
        const chartRetry: number[] = [];

        onlyBotWithStats.forEach((stat: Stat[]) => {
          stat.forEach((statBis: Stat, i: number) => {
            if (!chartSuccess[i]) {
              chartSuccess[i] = 0;
              chartErrors[i] = 0;
              chartRetry[i] = 0;
            }
            chartSuccess[i] += statBis.success;
            chartErrors[i] += statBis.failed;
            chartRetry[i] += statBis.retry;
          });
        });

        this.barChartData = {
          labels: date,
          datasets: [
            {
              data: chartSuccess,
              label: $localize`Succès`,
              borderColor: '#1de9b6',
              pointBackgroundColor: '#1de9b6',
            },
            {
              data: chartErrors,
              label: $localize`Echecs`,
              borderColor: '#f44336',
              pointBackgroundColor: '#f44336',
            },
            {
              data: chartRetry,
              label: $localize`Relances`,
              borderColor: '#a4a4a4',
              pointBackgroundColor: '#a4a4a4',
            },
          ],
        };
      });
  }

  private setMidnight(date: Date): Date {
    date.setHours(0, 0, 0, 0);
    return date;
  }

  private minusSevenDays(date: Date): Date {
    return new Date(
      new Date(date.setDate(date.getDate() - 7)).setHours(0, 0, 0, 0)
    );
  }

  private yesterday(date: Date): Date {
    return new Date(
      new Date(date.setDate(date.getDate() - 1)).setHours(23, 59, 59, 999)
    );
  }
}
